import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import LangSwitcher from "../components/LangSwitcher";
import { Mail, Lock, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, totp || undefined);
      navigate("/app");
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      if (detail === "2FA code required") {
        setNeeds2fa(true);
        toast.info("Introdueix el codi 2FA");
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-5">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-5 left-5">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-hosys.png" alt="HO-SYS"
               className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display font-bold">HO-SYS</span>
        </Link>
      </div>
      <div className="absolute top-5 right-5"><LangSwitcher /></div>

      <div className="relative w-full max-w-md card-surface p-8 sm:p-10">
        <h1 className="font-display text-3xl font-bold tracking-tighter mb-2">
          {t("auth.login")}
        </h1>
        <p className="text-sm text-slate-400 mb-7">{t("landing.tag")}</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
              {t("auth.email")}
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#05070D] border-white/10 pl-9 h-11"
                placeholder="tu@empresa.com"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
              {t("auth.password")}
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#05070D] border-white/10 pl-9 h-11"
                placeholder="••••••••"
              />
            </div>
          </div>
          {needs2fa && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                {t("auth.totp_label")}
              </label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  data-testid="login-totp"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  maxLength={6}
                  inputMode="numeric"
                  className="bg-[#05070D] border-white/10 pl-9 h-11 tracking-widest"
                  placeholder="000000"
                />
              </div>
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {t("auth.submit_login")}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/forgot-password"
                className="text-xs text-slate-400 hover:text-blue-300 underline-offset-2 hover:underline"
                data-testid="link-forgot-password">
            {t("auth.forgot_password")}
          </Link>
        </div>

        <div className="mt-6 text-sm text-slate-400 text-center">
          {t("auth.no_account")}{" "}
          <Link to="/register" className="text-blue-400 hover:text-blue-300" data-testid="link-register">
            {t("auth.register")}
          </Link>
        </div>
      </div>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}
